import type { CSSProperties, ReactNode } from 'react'
import { SectionLabel } from './SectionLabel'

interface DetailPanelProps {
  /** Mono uppercase context line (e.g. "STIIIZY WEST HOLLYWOOD · 1.2 MI · BLOCKED"). */
  eyebrow?: ReactNode
  /** Display-serif headline. */
  title?: ReactNode
  className?: string
  style?: CSSProperties
  children?: ReactNode
}

/**
 * DetailPanel — the right-side inspector / inline-expansion shell used by the
 * Alert Feed, Competitor Profile and Price deep-dives: mono eyebrow + display
 * serif title + stacked sections. Aurora+ source: `.li-inline` (s12).
 */
export function DetailPanel({ eyebrow, title, className, style, children }: DetailPanelProps) {
  return (
    <div className={className} style={style}>
      {eyebrow != null && (
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 9,
            fontWeight: 600,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--text-3)',
            padding: '0 0 4px',
          }}
        >
          {eyebrow}
        </div>
      )}
      {title != null && (
        <div
          style={{
            fontFamily: 'var(--display)',
            fontSize: 21,
            fontWeight: 400,
            lineHeight: 1.2,
            color: 'var(--text-1)',
            letterSpacing: '-0.01em',
            marginBottom: 14,
          }}
        >
          {title}
        </div>
      )}
      {children}
    </div>
  )
}

interface DetailSectionProps {
  label?: ReactNode
  /** Accent-bordered callout (used for the locked "Recommendation" block). */
  variant?: 'plain' | 'recommendation' | 'recommendation-muted'
  style?: CSSProperties
  children?: ReactNode
}

/**
 * DetailSection — one labeled block inside a DetailPanel. `recommendation`
 * renders the accent-bordered callout; `recommendation-muted` is the
 * no-action-needed variant. Aurora+ source: `.det-sec` / `.det-rec` (s12).
 */
export function DetailSection({ label, variant = 'plain', style, children }: DetailSectionProps) {
  const callout = variant !== 'plain'
  const muted = variant === 'recommendation-muted'

  const wrap: CSSProperties = callout
    ? {
        marginBottom: 14,
        padding: '10px 12px 11px 13px',
        background: muted ? 'var(--surface-3)' : 'var(--accent-soft)',
        border: `1px solid ${muted ? 'var(--border-2)' : 'rgba(11,184,184,0.20)'}`,
        borderLeft: `3px solid ${muted ? 'var(--text-3)' : 'var(--accent)'}`,
        borderRadius: 8,
      }
    : { marginBottom: 14 }

  return (
    <div style={{ ...wrap, ...style }}>
      {label != null && (
        <SectionLabel
          style={{ marginBottom: 6, color: callout && !muted ? 'var(--accent)' : undefined }}
        >
          {label}
        </SectionLabel>
      )}
      <div
        style={{
          fontFamily: 'var(--sans)',
          fontSize: 12.5,
          lineHeight: 1.5,
          color: muted ? 'var(--text-2)' : 'var(--text-1)',
        }}
      >
        {children}
      </div>
    </div>
  )
}
